import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useWizardSteps from '../wizard-v2/useWizardSteps'
import classNames from 'classnames'
import { setCurrentStep, setWizardType } from '../wizard-v2/onboardingSlice'
import useBodyScrollLock from '../../hooks/useBodyScrollLock'
import { t } from '../../utilities/translator'
import { getWizardV2State } from '../wizard-v2/wizardService'
import useOnClickOutside from '../../hooks/useOnClickOutside'
import WizardSlider from '../wizard-v2/WizardSlider'
import { pushEvent } from '../../utilities/analytics'
import { ONBOARDING_SKIP_HOME_EVENT, ONBOARDING_STEP_LOADED_EVENT, ONBOARDING_V2_SKIP_EMAIL_EVENT, WIZARD_FEED_BUILDER_CLICKED } from '../../constants/analyticEvents'
import useSearchPerformed from '../../hooks/useSearchPerformed'

const WizardModal = ({
  closeModal,
}) => {
  const modalRef = useRef(null);
  const dispatch = useDispatch();
  const { stepNames, currentStep, showDesktop } = useSelector((state) => state.onboarding);
  const stepsList = useWizardSteps();
  const currentStepIndex = stepNames.findIndex(step => step === currentStep);
  const stepConfig = getWizardV2State();
  const { stepsToShow } = stepConfig;
  const showGroupBookingModal = useSelector((state) => state.app.showGroupBookingModal);

  const [setAppTimeout] = useSearchPerformed();
  useOnClickOutside(modalRef, (window.location.search.indexOf('wizardWidget=true') > -1 || showGroupBookingModal) ? (() => false) : closeModal);

  const handlePrevStepClick = () => {
    dispatch(setCurrentStep(-1));
  }

  useBodyScrollLock({ condition: true })

  useEffect(() => {
    if (showDesktop) {
      const stepName = currentStep;
      if (stepName === 'who') {
        setTimeout(() => {
          if (window.pr_welcome) {
            pushEvent(ONBOARDING_STEP_LOADED_EVENT, {
              step: currentStepIndex,
              stepName,
              stepsToShow,
              skipAvailable: !!localStorage.getItem('onboardingCompleted'),
              welcomePresets: window.pr_welcomeScreenToLoad,
            });
          } else {
            pushEvent(ONBOARDING_STEP_LOADED_EVENT, {
              step: currentStepIndex,
              stepName,
              stepsToShow,
              skipAvailable: !!localStorage.getItem('onboardingCompleted'),
            });
          }
        }, 500);
      } else if (stepName === 'email') {
        if (window.pr_welcome) {
          pushEvent(ONBOARDING_STEP_LOADED_EVENT, {
            step: currentStepIndex,
            stepName,
            stepsToShow,
            checkboxEnabled: window.holidayFinder.abTest?.emailStepWithMarketing,
            skipAvailable: !!localStorage.getItem('onboardingCompleted'),
            welcomePresets: window.pr_welcomeScreenToLoad,
          });
        } else {
          pushEvent(ONBOARDING_STEP_LOADED_EVENT, {
            step: currentStepIndex,
            stepName,
            stepsToShow,
            checkboxEnabled: window.holidayFinder.abTest?.emailStepWithMarketing,
            skipAvailable: !!localStorage.getItem('onboardingCompleted'),
          });
        }
      } else {
        if (window.pr_welcome) {
          pushEvent(ONBOARDING_STEP_LOADED_EVENT, {
            step: currentStepIndex,
            stepName,
            stepsToShow,
            skipAvailable: !!localStorage.getItem('onboardingCompleted'),
            welcomePresets: window.pr_welcomeScreenToLoad,
          });
        } else {
          pushEvent(ONBOARDING_STEP_LOADED_EVENT, {
            step: currentStepIndex,
            stepName,
            stepsToShow,
            skipAvailable: !!localStorage.getItem('onboardingCompleted'),
          });
        }
        if (stepName === 'loading') {
          setAppTimeout(4000);
        }
      }
    }
  }, [currentStep])

  const getModalTitle = () => {
    switch (currentStep) {
      case 'who':
      case 'adults':
      case 'children':
        return stepConfig.whoStep?.desktopTitle

      case 'when':
        return stepConfig.whenStep?.desktopTitle

      case 'nights':
        return stepConfig.nightsStep?.desktopTitle

      case 'whatTheme':
        return stepConfig.whatThemeStep?.desktopTitle

      case 'hostingLevelPref':
        return stepConfig.hostingLevelPrefStep?.desktopTitle

      case 'email':
        return stepConfig.emailStep?.desktopTitle

      case 'similarDestinations':
        return stepConfig.similarDestinationsStep?.desktopTitle

      case 'departure':
        return  stepConfig.departureStep?.desktopTitle

      default:
        return stepConfig.whoStep?.desktopTitle
    }
  }

  const onCloseClick = () => {
    if (currentStep === 'email') {
      dispatch(setCurrentStep(1));
      pushEvent(ONBOARDING_SKIP_HOME_EVENT, { skipFrom: 'email step close modal click' })
      pushEvent(ONBOARDING_V2_SKIP_EMAIL_EVENT, { skipFrom: 'email step close modal click' })
    } else {
      closeModal()
    }
  }

  const handleFeedBuilderClick = () => {
    dispatch(setWizardType('wizard'));
    pushEvent(WIZARD_FEED_BUILDER_CLICKED);
  };

  return (
    <>
      <div className="modal-container">
        <div className="wizard-modal-overlay" />
        <div className="wizard-modal" ref={modalRef}>
          {currentStep !== 'loading' && (
            <div className="wizard-modal__header p-5 flex items-center">
              {/* Hidden by Bruno's request */}
              {/* <span>{getModalTitle()}</span> */}
              {(window.location.search.indexOf('wizardWidget=true') === -1) &&
                <div className="p-2 rounded-full bg-shark-50 flex ms-auto text-xl cursor-pointer text-tertiary hover:text-primary" onClick={onCloseClick}>
                  <i className="ri-close-line leading-none" />
                </div>
              }
            </div>
          )}
          <div className="wizard-modal__body relative">
            <WizardSlider>
              {stepsList[currentStepIndex]?.component}
            </WizardSlider>
          </div>
          {(currentStep !== 'loading' && currentStep !== 'wizardTypeSelector' && currentStep !== 'loading-ai') && (
            <div className="wizard-modal__footer p-5 flex justify-between items-center relative">
              {currentStepIndex !== 0 && currentStep !== 'assistant' && (
                <button className="wizard-btn wizard-btn--back" onClick={handlePrevStepClick} disabled={currentStepIndex === 0}>
                  <i className="ri-arrow-left-s-line text-xl leading-none rtl:-scale-x-100" />
                  {t('onboarding.back')}
                </button>
              )}
              {currentStep === 'assistant' && (
                <button className="feed-builder-btn--desktop" onClick={handleFeedBuilderClick}>{t('onboarding.assistant.feedBuilderBtn')}</button>
              )}
              {currentStep !== 'assistant' && (
                <div className="wizard-dots">
                  {stepNames.map((_, index) => (
                    <div key={_} className={classNames('bullet', { 'bullet--active': index === currentStepIndex })} />
                  ))}
                </div>
              )}
              <div id="wizard-next-button" />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default WizardModal
